/**
 * Security utilities for input sanitization and XSS protection
 */

/**
 * Sanitize email input to prevent injection attacks
 * - Removes HTML tags
 * - Removes script tags and dangerous characters
 * - Trims whitespace
 * - Limits length
 * @param {string} email - Raw email input
 * @returns {string} Sanitized email
 */
export const sanitizeEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return '';
  }

  return email
    .trim()
    // Remove any HTML tags
    .replace(/<[^>]*>/g, '')
    // Remove script content
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    // Remove any javascript: or data: protocols
    .replace(/javascript:/gi, '')
    .replace(/data:/gi, '')
    // Remove any null bytes
    .replace(/\0/g, '')
    // Remove non-printable characters
    .replace(/[\x00-\x1F\x7F]/g, '')
    // Limit length to prevent buffer overflow attacks
    .substring(0, 254); // RFC 5321 max email length
};

/**
 * Escape HTML characters to prevent XSS when displaying user input
 * @param {string} text - Text to escape
 * @returns {string} Escaped text
 */
export const escapeHtml = (text) => {
  if (!text || typeof text !== 'string') {
    return '';
  }

  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
  };

  return text.replace(/[&<>"'/]/g, (char) => map[char]);
};

/**
 * Validate email format with strict regex
 * More secure than basic validation
 * @param {string} email - Email to validate
 * @returns {boolean} True if valid email format
 */
export const isValidEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return false;
  }

  // RFC 5322 compliant regex (simplified but secure)
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  // Additional checks
  const minLength = 3; // a@b
  const maxLength = 254; // RFC 5321

  if (email.length < minLength || email.length > maxLength) {
    return false;
  }

  // Check for valid format
  if (!emailRegex.test(email)) {
    return false;
  }

  // Check for consecutive dots
  if (/\.\./.test(email)) {
    return false;
  }

  // Check for valid TLD (at least 2 characters)
  const parts = email.split('@');
  if (parts.length !== 2) {
    return false;
  }

  const domain = parts[1];
  const domainParts = domain.split('.');
  const tld = domainParts[domainParts.length - 1];

  if (tld.length < 2) {
    return false;
  }

  return true;
};

/**
 * Validate and sanitize language code to prevent path traversal
 * @param {string} lang - Language code
 * @returns {string} Safe language code or 'en' as default
 */
export const sanitizeLanguageCode = (lang) => {
  if (!lang || typeof lang !== 'string') {
    return 'en';
  }

  // Only allow specific language codes (whitelist approach)
  // TEMP: 'vi' disabled
  const allowedLanguages = ['en', 'id', 'jp', 'ko', 'zh'];
  const sanitized = lang.toLowerCase().replace(/[^a-z]/g, '').substring(0, 2);

  return allowedLanguages.includes(sanitized) ? sanitized : 'en';
};

/**
 * Detect potential SQL injection patterns
 * @param {string} input - Input to check
 * @returns {boolean} True if suspicious patterns detected
 */
export const hasSqlInjectionPattern = (input) => {
  if (!input || typeof input !== 'string') {
    return false;
  }

  const sqlPatterns = [
    /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|EXECUTE|UNION|DECLARE)\b)/i,
    /(--|\#|\/\*|\*\/)/,
    /('|";|";--)/,
    /(\bOR\b.*=.*)/i,
    /(\bAND\b.*=.*)/i,
  ];

  return sqlPatterns.some(pattern => pattern.test(input));
};

/**
 * Detect potential XSS patterns
 * @param {string} input - Input to check
 * @returns {boolean} True if suspicious patterns detected
 */
export const hasXssPattern = (input) => {
  if (!input || typeof input !== 'string') {
    return false;
  }

  const xssPatterns = [
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    /javascript:/gi,
    /on\w+\s*=/gi, // Event handlers like onclick=
    /<iframe/gi,
    /<object/gi,
    /<embed/gi,
    /eval\(/gi,
    /expression\(/gi,
  ];

  return xssPatterns.some(pattern => pattern.test(input));
};

/**
 * Comprehensive input validation for email field
 * Returns object with validation result and sanitized value
 * @param {string} email - Email input to validate
 * @returns {object} { isValid: boolean, sanitized: string, error: string|null }
 */
export const validateAndSanitizeEmail = (email) => {
  // First sanitize
  const sanitized = sanitizeEmail(email);

  // Check for injection attempts
  if (hasSqlInjectionPattern(sanitized) || hasXssPattern(sanitized)) {
    return {
      isValid: false,
      sanitized: '',
      error: 'Invalid characters detected',
    };
  }

  // Validate format
  if (!isValidEmail(sanitized)) {
    return {
      isValid: false,
      sanitized,
      error: 'Invalid email format',
    };
  }

  return {
    isValid: true,
    sanitized,
    error: null,
  };
};
